import { KeyRound, Lock, LogIn, ShieldCheck } from 'lucide-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { authApi } from '@/lib/api'
import { ApiError } from '@/lib/api/errors'
import { useAuth } from '@/lib/auth/context'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

type Step = 'credentials' | 'code'

export function ScoreReauthDialog() {
  const { t } = useTranslation()
  const auth = useAuth()

  const [step, setStep] = React.useState<Step>('credentials')
  const [username, setUsername] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [code, setCode] = React.useState('')
  const [challengeToken, setChallengeToken] = React.useState<string | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const [pending, setPending] = React.useState(false)

  const onOpenChange = (open: boolean) => {
    if (!open) {
      auth.resolveScoreReauth(null)
      reset()
    }
  }

  const reset = () => {
    setStep('credentials')
    setUsername('')
    setPassword('')
    setCode('')
    setChallengeToken(null)
    setError(null)
    setPending(false)
  }

  React.useEffect(() => {
    if (!auth.scoreReauthOpen) reset()
  }, [auth.scoreReauthOpen])

  const translateApiError = (err: unknown, fallback: string): string => {
    if (err instanceof ApiError) {
      if (err.status === 401) {
        if (step === 'code') return t('score.errors.invalidCode')
        return t('score.errors.invalid')
      }
      if (err.status >= 500 || err.status === 502)
        return t('score.errors.upstream')
      if (typeof err.detail === 'string' && err.detail) return err.detail
    }
    return fallback
  }

  const handleStartLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setPending(true)
    setError(null)
    try {
      const response = await authApi.scoreStartLogin({ username, password })
      setChallengeToken(response.challenge_token)
      setStep('code')
    } catch (err) {
      setError(translateApiError(err, t('common.error')))
    } finally {
      setPending(false)
    }
  }

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!challengeToken) return
    setPending(true)
    setError(null)
    try {
      const response = await authApi.scoreVerifyChallenge({
        challenge_token: challengeToken,
        code,
      })
      auth.setScoreSession(
        response.session_token,
        response.session_expires_at ?? null,
      )
      auth.resolveScoreReauth(response.session_token)
      reset()
    } catch (err) {
      setError(translateApiError(err, t('common.error')))
    } finally {
      setPending(false)
    }
  }

  const handleResend = async () => {
    setStep('credentials')
    setCode('')
    setChallengeToken(null)
    setError(null)
  }

  return (
    <Dialog open={auth.scoreReauthOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
            <ShieldCheck className="h-5 w-5" aria-hidden />
          </div>
          <DialogTitle className="text-center">
            {t('score.reauthTitle')}
          </DialogTitle>
          <DialogDescription className="text-center">
            {t('score.reauthDescription')}
          </DialogDescription>
        </DialogHeader>
        {error ? (
          <Alert variant="destructive">
            <AlertTitle>{t('common.error')}</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}
        {step === 'credentials' ? (
          <form onSubmit={handleStartLogin} className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="score-username">{t('score.usernameLabel')}</Label>
              <div className="relative">
                <Lock
                  className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
                  aria-hidden
                />
                <Input
                  id="score-username"
                  type="text"
                  autoFocus
                  autoComplete="username"
                  className="pl-8"
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="score-password">{t('score.passwordLabel')}</Label>
              <div className="relative">
                <KeyRound
                  className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
                  aria-hidden
                />
                <Input
                  id="score-password"
                  type="password"
                  autoComplete="current-password"
                  className="pl-8"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>
            <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={pending}
              >
                {t('common.cancel')}
              </Button>
              <Button type="submit" loading={pending}>
                {t('score.startLogin')}
              </Button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleVerify} className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="score-code">{t('score.codeLabel')}</Label>
              <Input
                id="score-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                {t('score.codeHint')}
              </p>
            </div>
            <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-between">
              <Button
                type="button"
                variant="ghost"
                onClick={handleResend}
                disabled={pending}
              >
                {t('score.resend')}
              </Button>
              <Button type="submit" loading={pending}>
                <LogIn className="mr-1.5 h-4 w-4" />
                {t('score.verify')}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
